import { notification, Title, Tooltip } from "components/common";
import {
  ADD_ASSESSMENT,
  COPY_ASSESSMENT,
  DELETE_ICON,
  EDIT_ASSESSMENT,
  PIN,
} from "components/svg";
import { removeTagsAndStyling } from "configs/utils";
import CircularArrow from "features/presentation/components/arrowSlider";
import { useUpdateScriptMutation } from "features/script/api";
import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { setScriptsDetailData } from "store/scriptDetailSlice";
import AssessmentAndPlanPanel from "../addScriptForm/assessmentAndPlanPanel";
import styles from "./index.module.less";

const ScriptDetailAssessmentPlanSection = ({ details, previewMode }) => {
  const user = useSelector((state) => state.app.user?.info);
  const [selectedAssessment, setSelectedAssessment] = useState(0);
  const dispatch = useDispatch();

  const [updateData, { isLoading: updateLoading }] = useUpdateScriptMutation();

  const handleAddEditAssessment = async (updatedData, message) => {
    const tempDetails = JSON.parse(JSON.stringify(details));
    const tempUpdatedData = JSON.parse(JSON.stringify(updatedData));
    const response = await updateData({
      ...tempDetails,
      assessment_and_plans: tempDetails?.assessment_and_plans?.length
        ? tempDetails?.assessment_and_plans.map((obj) => {
            if (obj.hasOwnProperty("editor")) delete obj["editor"];
            return obj;
          })
        : [],
      ...(updatedData && {
        assessment_and_plans: tempUpdatedData?.length
          ? tempUpdatedData.map((obj) => {
              if (obj.hasOwnProperty("editor")) delete obj["editor"];
              return obj;
            })
          : [],
      }),
      isUpdatingAssessments: true,
    });
    if (response) {
      if (response?.error) {
        notification.error({ message: response?.error?.message });
      } else {
        message && notification.success({ message: message });
        updatedData && setSelectedAssessment(0);
      }
    }
  };

  const handleChangePin = () => {
    let tempAssessments = JSON.parse(
      JSON.stringify(details?.assessment_and_plans)
    );
    tempAssessments = tempAssessments.map((ass, i) => {
      if (i != selectedAssessment) {
        return {
          ...ass,
          ...(ass?.pinnedBy?.length
            ? {
                pinnedBy: ass?.pinnedBy?.filter((e) => e == user?.id),
              }
            : {}),
        };
      } else {
        return ass;
      }
    });
    //Updating its pinned for the selected assessment
    tempAssessments = tempAssessments.map((ass, i) => {
      return {
        ...ass,
        ...(i == selectedAssessment
          ? {
              pinnedBy: !!ass?.pinnedBy?.find((e) => e == user?._id)
                ? ass?.pinnedBy?.filter((e) => e != user?._id)
                : [...ass?.pinnedBy, user?._id],
            }
          : {}),
      };
    });
    handleAddEditAssessment(
      tempAssessments,
      !!tempAssessments?.[selectedAssessment]?.pinnedBy?.find(
        (e) => e == user?._id
      )
        ? "Pinned Successfully!"
        : "Pin Removed Successfully!"
    );
  };

  const handleChange = (key, value) => {
    dispatch(setScriptsDetailData({ [key]: value }));
  };

  const handleAddAssessmentAndPlan = () => {
    let tempAssessments = JSON.parse(
      JSON.stringify(details?.assessment_and_plans)
    );
    tempAssessments?.push({
      key: tempAssessments?.length?.toString(),
      description: "",
      createdBy: user?._id,
      userName: user?.firstname + " " + user?.lastname,
      pinnedBy: [],
      editor: true,
    });
    handleChange("assessment_and_plans", tempAssessments);
    setSelectedAssessment(details?.assessment_and_plans?.length);
  };

  const handleEdit = () => {
    let tempAssessments = JSON.parse(
      JSON.stringify(details?.assessment_and_plans)
    );
    tempAssessments[selectedAssessment] = {
      ...tempAssessments?.[selectedAssessment],
      editor: !tempAssessments?.[selectedAssessment]?.editor,
    };
    handleChange("assessment_and_plans", tempAssessments);
  };

  const handleDeleteAP = () => {
    let tempAssessments = JSON.parse(
      JSON.stringify(details?.assessment_and_plans)
    );
    handleAddEditAssessment(
      tempAssessments.filter((e, i) => i != selectedAssessment),
      "Deleted Successfully!"
    );
  };

  const handleCopyToClipboard = () => {
    let text = removeTagsAndStyling(
      details?.assessment_and_plans?.[selectedAssessment]?.description
    );
    if (!text) return null;
    const textarea = document.createElement("textarea");
    textarea.value = text;
    textarea.style.position = "absolute";
    textarea.style.left = "-9999px";
    document.body.appendChild(textarea);
    textarea.select();
    document.execCommand("copy");
    document.body.removeChild(textarea);
    notification.success({ message: "Copied Successfully!" });
  };

  const canEditOrDelete = () => {
    return (
      details?.assessment_and_plans?.[selectedAssessment]?.createdBy ==
        user?._id || user?.user_role == "ADMIN"
    );
  };

  const assessmentActions = [
    {
      title: "Pin/UnPin",
      onClick: handleChangePin,
      getIcon: () => (
        <PIN
          clr={
            details?.assessment_and_plans?.[selectedAssessment]?.pinnedBy?.find(
              (e) => e == user?._id
            )
              ? "#1565DC"
              : "#808080"
          }
        />
      ),
    },
    {
      title: "Add",
      onClick: handleAddAssessmentAndPlan,
      getIcon: () => <ADD_ASSESSMENT />,
    },
    ...(canEditOrDelete()
      ? [
          {
            title: "Edit",
            onClick: handleEdit,
            getIcon: () => <EDIT_ASSESSMENT />,
          },
        ]
      : []),
    {
      title: "Copy Text",
      onClick: handleCopyToClipboard,
      getIcon: () => <COPY_ASSESSMENT />,
    },
    ...(canEditOrDelete()
      ? [
          {
            title: "Delete",
            onClick: handleDeleteAP,
            getIcon: () => <DELETE_ICON />,
          },
        ]
      : []),
  ];
  return (
    <div
      className={styles.titleDescription}
      id={`Assessment and Plan`}
      key={`Assessment and Plan`}
    >
      <div className={styles.title}>
        <Title level={3}>
          Assessment And Plan ({details?.assessment_and_plans?.length})
        </Title>
        {!previewMode && (
          <div className={styles.actions}>
            {details?.assessment_and_plans?.length ? (
              <CircularArrow
                totalSlides={details?.assessment_and_plans?.length}
                subSlide={selectedAssessment}
                setSubSlide={(e) => setSelectedAssessment(e)}
              />
            ) : null}
            {assessmentActions.map((e) => {
              return (
                <Tooltip title={e?.title}>
                  <div onClick={e?.onClick} className={styles.circle}>
                    {e?.getIcon()}
                  </div>
                </Tooltip>
              );
            })}
          </div>
        )}
      </div>
      <AssessmentAndPlanPanel
        index={selectedAssessment}
        data={details?.assessment_and_plans?.[selectedAssessment]}
        assessments={details?.assessment_and_plans}
        setSelectedAssessment={setSelectedAssessment}
        handleChange={handleChange}
        keyValue={"assessment_and_plans"}
        detailPage={true}
        previewMode={previewMode}
        onSubmitAssessment={handleAddEditAssessment}
      />
    </div>
  );
};

export default ScriptDetailAssessmentPlanSection;
